import React, { useState } from 'react';
import { Container, Table, Button, Form, Row, Col, Card, Modal } from 'react-bootstrap';
import { Checkbox, Select } from 'antd';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import DateRangePicker from '@/common/DateRangePicker/DateRangePicker';
import FormItem from '@/common/FormItem/FormItem';
import dayjs from 'dayjs';
import TitleHelmet from '@/components/TitleHelmet';
import PageTitle from '@/common/PageTitle/PageTitle';
import BrandButton from '@/common/BrandButton/BrandButton';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import styles from './talents.module.scss';


interface Talent {
  id: string;
  name: string;
  bio: string;
  soundbyte: string;
  image: string;
  category: string;
  createdAt: string;
  updatedAt: string;
  slug: string;
}

const mockTalents: Talent[] = [
  {
    id: '1',
    name: 'James',
    bio: 'Radio personality and voice talent',
    soundbyte: 'https://example.com/soundbyte1.mp3',
    image: 'https://example.com/image1.jpg',
    category: 'Radio',
    createdAt: '2024-01-15',
    updatedAt: '2024-01-20',
    slug: 'james'
  },
  {
    id: '2',
    name: 'PhiLL',
    bio: 'Music producer and DJ',
    soundbyte: 'https://example.com/soundbyte2.mp3',
    image: 'https://example.com/image2.jpg',
    category: 'Music',
    createdAt: '2024-01-10',
    updatedAt: '2024-01-18',
    slug: 'phill'
  },
  {
    id: '3',
    name: 'Alicia',
    bio: 'Singer and songwriter',
    soundbyte: 'https://example.com/soundbyte3.mp3',
    image: 'https://example.com/image3.jpg',
    category: 'Music',
    createdAt: '2024-01-12',
    updatedAt: '2024-01-19',
    slug: 'alicia'
  },
  {
    id: '4',
    name: 'Sam',
    bio: 'Voice actor and narrator',
    soundbyte: 'https://example.com/soundbyte4.mp3',
    image: 'https://example.com/image4.jpg',
    category: 'Voice',
    createdAt: '2024-01-08',
    updatedAt: '2024-01-16',
    slug: 'sam'
  },
  {
    id: '5',
    name: 'Heidi',
    bio: 'Radio host and personality',
    soundbyte: 'https://example.com/soundbyte5.mp3',
    image: 'https://example.com/image5.jpg',
    category: 'Radio',
    createdAt: '2024-01-14',
    updatedAt: '2024-01-21',
    slug: 'heidi'
  },
  {
    id: '6',
    name: 'Arianna',
    bio: 'Podcast host and commentator',
    soundbyte: 'https://example.com/soundbyte6.mp3',
    image: 'https://example.com/image6.jpg',
    category: 'Podcast',
    createdAt: '2024-01-11',
    updatedAt: '2024-01-17',
    slug: 'arianna'
  },
  {
    id: '7',
    name: 'Scotty',
    bio: 'Sports commentator and analyst',
    soundbyte: 'https://example.com/soundbyte7.mp3',
    image: 'https://example.com/image7.jpg',
    category: 'Sports',
    createdAt: '2024-01-13',
    updatedAt: '2024-01-22',
    slug: 'scotty'
  }
];

const Talents: React.FC = () => {
  const [talents, setTalents] = useState<Talent[]>(mockTalents);
  const [selectedTalent, setSelectedTalent] = useState<Talent | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNewTalentModal, setShowNewTalentModal] = useState(false);
  const [dateRange, setDateRange] = useState<any>(null);
  const [newTalentForm, setNewTalentForm] = useState({
    name: '',
    category: '',
    bio: '',
    soundbyte: '',
    image: ''
  });
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const [checkAll, setCheckAll] = useState(false);
  const [filters, setFilters] = useState({
    name: null as string | null,
    category: null as string | null
  });
  const [resetCounter, setResetCounter] = useState(0);

  // Filter options
  const filterOptions = {
    name: talents.map(talent => ({ value: talent.name, label: talent.name })),
    category: Array.from(new Set(talents.map(talent => talent.category))).map(category => ({ value: category, label: category }))
  };

  const handleEdit = (talent: Talent) => {
    setSelectedTalent(talent);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedTalent(null);
  };

  const handleUpdateTalent = () => {
    if (!selectedTalent) return;

    // Get the form values from the modal
    const form = document.querySelector('#edit-talent-form') as HTMLFormElement;
    if (!form) return;

    const formData = new FormData(form);
    const updatedTalent: Talent = {
      ...selectedTalent,
      name: formData.get('name') as string || selectedTalent.name,
      category: formData.get('category') as string || selectedTalent.category,
      bio: formData.get('bio') as string || selectedTalent.bio,
      updatedAt: new Date().toISOString().split('T')[0]
    };

    setTalents(prev => prev.map(t => t.id === selectedTalent.id ? updatedTalent : t));
    setShowEditModal(false);
    setSelectedTalent(null);
  };

  const handleNewTalent = () => {
    setShowNewTalentModal(true);
  };

  const handleCloseNewTalentModal = () => {
    setShowNewTalentModal(false);
    // Reset form when closing
    setNewTalentForm({
      name: '',
      category: '',
      bio: '',
      soundbyte: '',
      image: ''
    });
  };

  const handleCreateTalent = () => {
    if (!newTalentForm.name.trim()) {
      alert('Please enter a name for the talent');
      return;
    }

    const newTalent: Talent = {
      id: Date.now().toString(),
      name: newTalentForm.name,
      category: newTalentForm.category,
      bio: newTalentForm.bio,
      soundbyte: newTalentForm.soundbyte,
      image: newTalentForm.image,
      createdAt: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0],
      slug: newTalentForm.name.toLowerCase().replace(/\s+/g, '-')
    };

    setTalents(prev => [...prev, newTalent]);
    setShowNewTalentModal(false);
    
    // Reset form
    setNewTalentForm({
      name: '',
      category: '',
      bio: '',
      soundbyte: '',
      image: ''
    });
  };

  const handleNewTalentFormChange = (field: string, value: string) => {
    setNewTalentForm(prev => ({
      ...prev,
      [field]: value
    }));
  };



  const handleDelete = (talentId: string) => {
    if (window.confirm('Are you sure you want to delete this talent?')) {
      setTalents(prev => prev.filter(t => t.id !== talentId));
    }
  };

  const handleDateChange = (dates: any) => {
    setDateRange(dates);
  };

  const handleFilterChange = (filterName: string, value: string | null) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      name: null,
      category: null
    });
    setDateRange(null);
    setResetCounter(prev => prev + 1);
  };

  const handleOnCheckAllChange = (e: any) => {
    setCheckAll(e.target.checked);
    if (e.target.checked) {
      setCheckedList(filteredTalents.map(talent => talent.id));
    } else {
      setCheckedList([]);
    }
  };

  const handleOnCheckboxChange = (e: any, talent: Talent) => {
    const { checked } = e.target;
    if (checked) {
      setCheckedList(prev => [...prev, talent.id]);
    } else {
      setCheckedList(prev => prev.filter(id => id !== talent.id));
    }
  };

  const filteredTalents = talents.filter(talent => {
    if (filters.name && !talent.name.toLowerCase().includes(filters.name.toLowerCase())) return false;
    if (filters.category && talent.category !== filters.category) return false;
    return true;
  });

  return (
    <React.Fragment>
      <TitleHelmet title="Talents" />
      <Container fluid>
        <Card>
          <Card.Body>
            <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
              <PageTitle title="Talents" />
              <Button 
                onClick={handleResetFilters}
                style={{ 
                  background: 'white', 
                  color: '#333', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontWeight: '500'
                }}
              >
                <FilterOutlined /> Reset All
              </Button>
            </Card.Title>

            {/* Filters Section */}
            <Form className={styles.form}>
              <Row>
                <Col>
                  <Row>
                    <Col md={4}>
                      {/* 
                        FIXED: Removed 'name' prop to avoid conflicts with external React state management.
                        This allows the Select component to clear properly on reset.
                      */}
                      <FormItem
                        label="Name"
                        className="customSelectWrapper"
                        layout="vertical"
                      >
                        <Select
                          key={`name-reset-${resetCounter}`}
                          showSearch
                          allowClear
                          className={`customSelect ${styles.selectField}`}
                          placeholder="Any talent name"
                          value={filters.name}
                          onChange={value => handleFilterChange('name', value)}
                          options={filterOptions.name}
                        />
                      </FormItem>
                    </Col>
                    <Col md={4}>
                      <FormItem
                        label="Category"
                        className="customSelectWrapper"
                        layout="vertical"
                      >
                        <Select
                          key={`category-reset-${resetCounter}`}
                          showSearch
                          allowClear
                          className={`customSelect ${styles.selectField}`}
                          placeholder="Any category"
                          value={filters.category}
                          onChange={value => handleFilterChange('category', value)}
                          options={filterOptions.category}
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form>
            
            <Row className="mb-2">
              <Col md={8}>
                <FormItem
                  label="Filter by Date Range"
                  layout="vertical"
                >
                  <DateRangePicker
                    key={`dateRange-reset-${resetCounter}`}
                    onChange={(dates) => {
                      if (dates && dates.length === 2) {
                        setDateRange([
                          dates[0] ? dates[0].toISOString() : '', 
                          dates[1] ? dates[1].toISOString() : ''
                        ]);
                      } else {
                        setDateRange(null);
                      }
                    }}
                    style={{ width: '100%' }}
                  />
                </FormItem>
              </Col>
              <Col md={4} className="d-flex align-items-center mt-2 mt-md-0">
                <Button 
                  className="w-100"
                  style={{ 
                    background: 'white', 
                    color: '#e21e26', 
                    border: '1px solid #e21e26', 
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    justifyContent: 'center',
                    height: '50px',
                    marginTop: '8px'
                  }}
                  onClick={handleNewTalent}
                >
                  <PlusOutlined style={{ fontSize: 16 }} />
                  Add New Talent
                </Button>
              </Col>
            </Row>
            
            {/* Talents Table */}
            <Row>
              <Col>
                <Table hover responsive className={styles.talentsTable}>
                  <thead>
                    <tr>
                      <th>
                        <Checkbox
                          checked={checkAll}
                          style={{ color: "#ffffff" }}
                          onChange={handleOnCheckAllChange}
                        />
                      </th>
                      <th style={{ color: "#ffffff", cursor: 'pointer' }}>
                        Name
                      </th>
                      <th style={{ color: "#ffffff", paddingLeft: '38px !important' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTalents.map((talent) => (
                      <tr key={talent.id}>
                        <td style={{ width: 40, textAlign: 'center', padding: '0 8px 0 20px', overflow: 'hidden' }}>
                          <div style={{ width: 24, height: 24, overflow: 'hidden', margin: '0 auto' }}>
                            <Checkbox 
                              checked={checkedList.some(id => id === talent.id)}
                              onChange={(e) => handleOnCheckboxChange(e, talent)} 
                              style={{ margin: 0, padding: 0, width: 24, height: 24 }} 
                            />
                          </div>
                        </td>
                        <td>{talent.name}</td>
                        <td>
                          <div className={styles.actionButtons}>
                            <Button 
                              variant="link" 
                              size="sm" 
                              className={styles.actionLink}
                            >
                              View
                            </Button>
                            <Button 
                              variant="link" 
                              size="sm" 
                              className={styles.actionLink}
                              onClick={() => handleEdit(talent)}
                            >
                              Edit
                            </Button>
                            <Button 
                              variant="link" 
                              size="sm" 
                              className={`${styles.actionLink} ${styles.deleteLink}`}
                              onClick={() => handleDelete(talent.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            </Row>

            {/* Footer */}
            <Row className="mt-3">
              <Col md={12} className="text-end">
                <span className={styles.displayInfo}>
                  Displaying all {filteredTalents.length} Talents
                </span>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>

      {/* Edit Talent Modal */}
      <Modal 
        show={showEditModal} 
        onHide={handleCloseEditModal}
        size="lg"
        className={styles.editModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Talent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTalent && (
            <Form id="edit-talent-form">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Name</strong></Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      defaultValue={selectedTalent.name}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Category</strong></Form.Label>
                    <Form.Control
                      type="text"
                      name="category"
                      defaultValue={selectedTalent.category}
                    />
                  </Form.Group>
                </Col>
              </Row>
                              <Form.Group className="mb-3">
                  <Form.Label><strong>Bio</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="bio"
                    defaultValue={selectedTalent.bio}
                  />
                </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label><strong>Demo (Soundbyte)</strong></Form.Label>
                <Form.Control
                  type="file"
                  accept="audio/*"
                />
                <Form.Text className="text-muted">
                  Current: {selectedTalent.soundbyte}
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label><strong>Image</strong></Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                />
                <Form.Text className="text-muted">
                  Current: {selectedTalent.image}
                </Form.Text>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateTalent}>
            Update Talent
          </Button>
        </Modal.Footer>
      </Modal>

      {/* New Talent Modal */}
      <Modal 
        show={showNewTalentModal} 
        onHide={handleCloseNewTalentModal}
        size="lg"
        className={styles.editModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>New Talent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Name</strong></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter talent name"
                    value={newTalentForm.name}
                    onChange={(e) => handleNewTalentFormChange('name', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Category</strong></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter category"
                    value={newTalentForm.category}
                    onChange={(e) => handleNewTalentFormChange('category', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label><strong>Bio</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Enter talent biography"
                value={newTalentForm.bio}
                onChange={(e) => handleNewTalentFormChange('bio', e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label><strong>Demo (Soundbyte)</strong></Form.Label>
              <Form.Control
                type="file"
                accept="audio/*"
              />
              <Form.Text className="text-muted">
                Upload an audio file for the talent's demo
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label><strong>Image</strong></Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
              />
              <Form.Text className="text-muted">
                Upload an image for the talent
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseNewTalentModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateTalent}>
            Create Talent
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default withAuthenticationRequired(Talents); 